import { Project } from '@/interfaces/project.interface';
import { useProjectStore } from '@/stores/useProjectStore';

// Save project by id
export function saveProjectToStorage(project: Project) {
  const setProject = useProjectStore.getState().setProject;
  setProject(project.id, project);
}

// Get project by id
export function getProjectFromStorage(projectId: string): Project | null {
  const getProject = useProjectStore.getState().getProject;
  return getProject(projectId);
}

// Get all cached projects
export function getAllProjectsFromStorage(): Project[] {
  const { projects } = useProjectStore.getState();
  return Object.values(projects)
    .filter((entry) => Date.now() - entry.timestamp <= 5 * 60 * 1000)
    .map((entry) => entry.data);
}

// Remove project by id
export function removeProjectFromStorage(projectId: string) {
  const { projects } = useProjectStore.getState();
  const newProjects = { ...projects };
  delete newProjects[projectId];
  useProjectStore.setState({ projects: newProjects });
}

// Queue project update for offline scenario
export function queueProjectUpdate(projectId: string, data: Partial<Project>) {
  const queueUpdate = useProjectStore.getState().queueUpdate;
  queueUpdate(projectId, data);
}

// Get all queued updates
export function getQueuedUpdates() {
  const getQueuedUpdates = useProjectStore.getState().getQueuedUpdates;
  return getQueuedUpdates();
}

// Remove queued update
export function removeQueuedUpdate(projectId: string) {
  const removeQueuedUpdate = useProjectStore.getState().removeQueuedUpdate;
  removeQueuedUpdate(projectId);
}

// Process offline queue when coming back online
export async function processOfflineQueue(
  updateProjectFn: (variables: {
    id: string;
    data: Partial<Project>;
  }) => Promise<unknown>
) {
  if (!navigator.onLine) {
    return;
  }

  const queuedUpdates = getQueuedUpdates();
  if (queuedUpdates.length === 0) {
    return;
  }

  const maxRetries = 3;
  const results = await Promise.allSettled(
    queuedUpdates.map(async (queuedUpdate) => {
      if (queuedUpdate.retries >= maxRetries) {
        // Remove from queue if max retries reached
        removeQueuedUpdate(queuedUpdate.id);
        return;
      }

      try {
        await updateProjectFn({
          id: queuedUpdate.id,
          data: queuedUpdate.data,
        });
        // Successfully processed, remove from queue
        removeQueuedUpdate(queuedUpdate.id);
      } catch (error) {
        // Increment retry count
        const store = useProjectStore.getState();
        const updatedQueue = store.offlineQueue.map((item) =>
          item.id === queuedUpdate.id
            ? { ...item, retries: item.retries + 1 }
            : item
        );
        useProjectStore.setState({ offlineQueue: updatedQueue });
        throw error;
      }
    })
  );

  return results;
}
